/**
 * Os afastamentos de câmera do mapa.
 *
 * É o esquema dos *Fire Emblem* de GameCube (*Path of Radiance*, *Radiant
 * Dawn*): a câmera não tem um cursor contínuo de zoom, tem posições fixas que
 * se alternam com um botão. Cada uma serve a um momento diferente da partida —
 * e é por isso que são degraus, e não uma barra:
 *
 * - **máximo** e **perto** para ler a cena: quem está ferido, qual é o terreno;
 * - **padrão** para jogar o turno;
 * - **amplo** e **panorama** para enxergar a frente antes de decidir o avanço.
 */

export interface ZoomLevel {
  readonly id: ZoomId;
  readonly label: string;
  readonly scale: number;
  /** O que este afastamento serve para fazer — vai no `title` do botão. */
  readonly hint: string;
}

export type ZoomId = 'maximo' | 'perto' | 'padrao' | 'amplo' | 'panorama';

/*
 * Cinco posições, do rosto da unidade ao mapa inteiro.
 *
 * Começou com três, como no GameCube. Cinco cobrem o que três deixavam de fora:
 * um degrau acima do "perto" para ler a ficha de quem está na casa, e um abaixo
 * do "amplo" para enxergar um mapa de 100×100 sem rolar a tela. Os valores
 * multiplicam a grade de 48px — 96, 72, 48, 34 e 24 pixels por casa.
 */
export const ZOOM_LEVELS: readonly ZoomLevel[] = [
  { id: 'maximo', label: 'Máximo', scale: 2, hint: 'Bem em cima — o detalhe do sprite e da casa' },
  { id: 'perto', label: 'Perto', scale: 1.5, hint: 'Câmera próxima — detalhe das unidades e do terreno' },
  { id: 'padrao', label: 'Padrão', scale: 1, hint: 'Câmera padrão — a grade no tamanho nativo do acervo' },
  { id: 'amplo', label: 'Amplo', scale: 0.7, hint: 'Câmera afastada — a frente inteira de uma vez' },
  { id: 'panorama', label: 'Panorama', scale: 0.5, hint: 'O mapa inteiro na tela, para planejar o avanço' },
];

/** O afastamento com que uma partida começa. */
export const DEFAULT_ZOOM: ZoomId = 'padrao';

/** O afastamento padrão, e o porto seguro de qualquer valor desconhecido. */
const FALLBACK = ZOOM_LEVELS.find((z) => z.id === 'padrao')!;

export function zoomLevel(id: ZoomId): ZoomLevel {
  return ZOOM_LEVELS.find((z) => z.id === id) ?? FALLBACK;
}

export function zoomScale(id: ZoomId): number {
  return zoomLevel(id).scale;
}

/**
 * O próximo afastamento na roda.
 *
 * `step` de +1 aproxima e −1 afasta, e a roda não dá a volta: chegar ao limite
 * e continuar apertando mantém onde está, em vez de saltar do mais próximo para
 * o mais distante — um salto desses desorienta no meio de um turno.
 */
export function stepZoom(current: ZoomId, step: 1 | -1): ZoomId {
  const index = ZOOM_LEVELS.findIndex((z) => z.id === current);
  const at = index < 0 ? ZOOM_LEVELS.indexOf(FALLBACK) : index;
  // A lista vai do mais próximo ao mais distante, então aproximar é recuar nela.
  const next = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, at - step));
  return ZOOM_LEVELS[next]!.id;
}

/** O afastamento seguinte, em roda fechada — o botão único que percorre todos. */
export function cycleZoom(current: ZoomId): ZoomId {
  const index = ZOOM_LEVELS.findIndex((z) => z.id === current);
  const at = index < 0 ? ZOOM_LEVELS.indexOf(FALLBACK) : index;
  return ZOOM_LEVELS[(at + 1) % ZOOM_LEVELS.length]!.id;
}
